import { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { getClientId, getLocationId } from '../../utils';
import useSettings from '../useSettings';

/** Events loaded per group for dates and age ranges on landing tiles (category chips use
 * `aggregatedCategories` from GET /groups). */
const EVENTS_PER_GROUP_FOR_TILES = 10;

function buildGroupTileMetadata(events: any[]) {
  const eventDates = events
    .map((event) => ({ start: event.startDate, end: event.endDate }))
    .filter((dateRange) => dateRange.start && dateRange.end)
    .sort(
      (left, right) =>
        new Date(left.start).getTime() - new Date(right.start).getTime(),
    );

  const registrationDates = events
    .map((event) => ({
      start: event.registrationStartDate,
      end: event.registrationEndDate,
    }))
    .filter((dateRange) => dateRange.start && dateRange.end)
    .sort(
      (left, right) =>
        new Date(left.start).getTime() - new Date(right.start).getTime(),
    );

  const ageRanges = events
    .map((event) => ({
      min: event.minMonthsRestrictions,
      max: event.maxMonthsRestrictions,
    }))
    .filter((ageRange) => ageRange.min !== undefined && ageRange.min !== null);

  return { events, eventDates, registrationDates, ageRanges };
}

/** Parent-portal filters: only events currently open for registration. */
function buildOpenRegistrationQuery(): string {
  const now = dayjs().toISOString();
  return `&filter.registrationStartDate=$lte:${now}&filter.registrationEndDate=$gte:${now}`;
}

const useEventGroups = () => {
  const [eventGroups, setEventGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const events_api_url = import.meta.env.VITE_EVENTS_API_URL;
  const client_id = getClientId();
  const locationId = getLocationId();
  const groups_limit = 50;
  const { hideAfterRegistrationClose } = useSettings();

  useEffect(() => {
    setLoading(true);

    // When client setting type=7 is on, only load currently enrollable events
    // for tile metadata (matches useEventFetching). Groups list still uses
    // hasActiveEvents=1 so empty/disabled/closed-only groups are excluded by API.
    const registrationFilter = hideAfterRegistrationClose
      ? buildOpenRegistrationQuery()
      : '';

    axios
      .get(
        `${events_api_url}/groups?filter.status=1&filter.hasActiveEvents=1&limit=${groups_limit}&filter.locationId=${locationId}`,
        {
          headers: {
            client_id: client_id,
          },
        },
      )
      .then(async (response) => {
        const responseArray = response.data.data.data;
        const groupsArray = Array.isArray(responseArray) ? responseArray : [];

        const groupsWithEvents = await Promise.all(
          groupsArray.map(async (group: any) => {
            try {
              const eventsResponse = await axios.get(
                `${events_api_url}/events?filter.status=1&filter.isVisible=1&filter.group=${group.id}&limit=${EVENTS_PER_GROUP_FOR_TILES}${registrationFilter}`,
                {
                  headers: {
                    client_id: client_id,
                  },
                },
              );
              const events = eventsResponse.data.data.data || [];
              return {
                ...group,
                ...buildGroupTileMetadata(events),
              };
            } catch (error) {
              console.error(`Error fetching events for group ${group.id}:`, error);
              return {
                ...group,
                ...buildGroupTileMetadata([]),
              };
            }
          }),
        );

        // Defense-in-depth: drop tiles that have no parent-visible events loaded.
        // Only apply when registration filtering is on, so a transient events
        // fetch failure does not hide groups when the setting is off.
        setEventGroups(
          hideAfterRegistrationClose
            ? groupsWithEvents.filter(
                (group) => Array.isArray(group.events) && group.events.length > 0,
              )
            : groupsWithEvents,
        );
      })
      .catch((error) => {
        console.error('Error fetching event groups:', error);
        setEventGroups([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [events_api_url, client_id, locationId, hideAfterRegistrationClose]);

  return { eventGroups, loading };
};

export default useEventGroups;
